/**
 * Parses a newline/comma-separated GTI string into a trimmed, non-empty array.
 * Used by FormCampaignEdit, BlocklistCreate, and BlocklistDetail to read the
 * hidden `{name}_valid` field written by FormGtiInput.
 */
export function parseGtisFromField (value: string): string[] {
  return value.split(/[\n,]/).map(g => g.trim()).filter(g => g.length > 0);
}

export function capitalizeFirstLetter (word: string) {
  return word.charAt(0).toUpperCase() + word.toLowerCase().slice(1);
}

/* eslint-disable @typescript-eslint/no-explicit-any */
type Obj = Record<string, any>;
const getNestedMessageFields = (message: Obj[], prefix: string = ''): Obj => (
  message.reduce((acc: Obj, obj: Record<string, any>) => {
    if (obj.children?.length) {
      return { ...acc, ...getNestedMessageFields(obj.children as Obj[], prefix + obj.property + '.') };
    } else {
      return { ...acc, [prefix + obj.property]: obj.constraints };
    }
  }, {})
);

export const getErrorsByField = (message: Obj[]) => {
  const errors = getNestedMessageFields(message);
  const errorsByField: Record<string, string> = {};

  for (const [field, constraints] of Object.entries(errors)) {
    let message = '';
    for (const constraint in constraints) {
      message += (constraints[constraint] + '. ');
    }
    message = message.slice(0, -1); // Remove the last space but keep the period

    errorsByField[field] = message;
  }

  return errorsByField;
};

/**
 * Meridian sets the `Image` `src` as an unquoted CSS URL:
 * https://code.amazon.com/packages/MeridianComponents/blobs/9e9e8ac57068e28f1baa488bcf13efc60b105a11/--/src/components/image/image.js#L31
 *
 * In rare cases we get special characters in a URL such as `'` which will not render
 * properly. We will see usage such as:
 *
 * ```
 * url(https://cloudfront.net/some'value)
 * ```
 *
 * While `'` is a valid character (and does not need escaping) in a URL, a
 * single `'` will break CSS processing. We escape the characters instead of
 * wrapping in quotes to handle more edge cases that might combine quotes.
 */
export const sanitizeMeridianImageSrc = (str: string) => str.replace(/(['"()])/g, '\\$1');
